import { useCallback, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { getMonthlyExpenseEstimate } from "../../services/monthlyExpense";
import { MonthEstimateAnswer } from "../../types/monthly-expense.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { todayInBangkok } from "../../utils/date";
import { useBranches } from "../SpecialOrder/useBranches";
import { useLatestAnswer } from "../DailyExpense/useLatestAnswer";
import ExpenseMonthPicker from "../DailyExpense/ExpenseMonthPicker";
import StateCard from "../DailyExpense/StateCard";
import SkeletonCard from "../DailyExpense/SkeletonCard";
import { API_MONTH_FORMAT, MONTH_ESTIMATE_MENU_TITLE } from "../DailyExpense/constants";
import { formatMonthLabel } from "../DailyExpense/format";
import EstimateDetail from "./EstimateDetail";

interface EstimateParams {
  month: string;
  branchCode: string | undefined;
}

/**
 * S5 ประมาณการเดือนนี้ — read-only. A branch role sees its own branch's estimate (the server takes
 * the branch from the token); head office names a branch first.
 */
export default function MonthEstimateScreen() {
  const { isHeadOfficeAccounting, isMaster } = useUserRole();
  const isHeadOffice = Boolean(isHeadOfficeAccounting || isMaster);
  const branches = useBranches(isHeadOffice);
  const [month, setMonth] = useState<Dayjs>(() => todayInBangkok().startOf("month"));
  const [branchCode, setBranchCode] = useState("");
  const yearMonth = month.format(API_MONTH_FORMAT);

  const params = useMemo<EstimateParams | null>(
    () => (isHeadOffice && !branchCode ? null : { month: yearMonth, branchCode: branchCode || undefined }),
    [isHeadOffice, branchCode, yearMonth],
  );
  const load = useCallback(
    async (query: EstimateParams): Promise<MonthEstimateAnswer> => {
      const response = await getMonthlyExpenseEstimate(query.month, query.branchCode);
      if (!response.data.data) throw new Error("Empty response");
      return response.data.data;
    },
    [],
  );
  const answer = useLatestAnswer(params, load);

  const handleMonthChange = (value: Dayjs | null) => {
    if (value?.isValid()) setMonth(value.startOf("month"));
  };
  const handleBranchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBranchCode(event.target.value);

  const renderBody = () => {
    if (params === null) {
      return <StateCard title="ยังไม่ได้เลือกสาขา" text="เลือกสาขาเพื่อดูประมาณการของเดือนนี้" />;
    }
    if (answer.isLoading) return <SkeletonCard />;
    if (answer.isFailed || !answer.data) {
      return (
        <StateCard title="โหลดประมาณการไม่สำเร็จ" text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่อีกครั้ง" onRetry={answer.reload} />
      );
    }
    if (!answer.data.estimate) {
      return (
        <StateCard
          title="ยังไม่มีประมาณการ"
          text={`จะขึ้นที่นี่หลัง HO ปิดเดือน${formatMonthLabel(answer.data.source_month)} และส่งยอดแล้ว`}
        />
      );
    }
    return <EstimateDetail data={answer.data} estimate={answer.data.estimate} />;
  };

  return (
    <Stack sx={{ py: 1 }} gap={2}>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={600}>
          {MONTH_ESTIMATE_MENU_TITLE}
        </Typography>
        <ExpenseMonthPicker value={month} onChange={handleMonthChange} />
        {isHeadOffice && (
          <TextField select size="small" label="สาขา" value={branchCode} onChange={handleBranchChange} sx={{ minWidth: 240 }}>
            {branches.map((branch) => (
              <MenuItem key={branch.branch_code} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {renderBody()}
    </Stack>
  );
}
